import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

// A cold work is judged by a model first, so the answer is not always instant. Comfortably
// above DownloadRedirect::PLAN_DEADLINE (2s) plus the bucketing write and two network
// legs: abort sooner and the server can record a redirect this tab never made.
const TIMEOUT_MS = 5000;

// The SWP has two implementations and they name their download surfaces differently:
// the legacy page has a toolbar and a sticky header, the block-composed one gives both
// its heroes one source. A new hero variant has to be added here or it opts out silently.
//
// An allowlist rather than a `swp-` prefix rule, which would sweep in the related-works
// grid (a different work), auto-downloads, and the bulk-download surfaces we skip.
const SWP_DOWNLOAD_SOURCES = new Set([
  'swp-toolbar',
  'swp-sticky-header',
  'swp-tabs-hero',
]);

export function isSwpDownloadSource(source: string): boolean {
  return SWP_DOWNLOAD_SOURCES.has(source);
}

// The upsell modal names its triggers `<source>-work-card-download-button`, and hands
// over the single paper when the reader closes it. The suffix is required, not just
// stripped: the pack buttons keep their bare source name, and `swp-tabs-hero` is one of
// them, so matching a bare source here would redirect on a pack purchase.
export function isSwpDownloadTrigger(trigger: string): boolean {
  const source = trigger.replace(/-work-card-download-button$/, '');
  return source !== trigger && isSwpDownloadSource(source);
}

interface AfterDownloadResponse {
  redirect: boolean;
  url?: string;
}

/**
 * Asks the server whether this reader should be sent into the paper trail for the work
 * they just downloaded. The server owns the decision — it resolves the A/B arm, and
 * enrolling the reader is what this call is for.
 *
 * @returns the URL to navigate to, or null to leave the page alone.
 */
export async function paperTrailRedirectUrl(
  workId: string | number,
): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetchWithCsrf(
      routes.api_v0_demo_day_paper_trail_after_download_path({
        subdomain_param: 'api',
      }),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ work_id: workId }),
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as AfterDownloadResponse;
    return body.redirect && body.url ? body.url : null;
  } catch {
    // Fails closed. A timed-out or failed check must leave the download alone.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export default paperTrailRedirectUrl;
